import { createShapeId } from "@tldraw/tlschema";
import type { Editor, VecLike } from "./canvas";
import type { TLImageShape, TLShape, TLShapeId } from "@tldraw/tlschema";
import type { Trace } from "../shared/schema";
import { fullRegion, traceCrop, type Rect } from "../shared/trace-geometry";

export const annotationTargets = new WeakMap<Editor, TLShapeId>();
export function editableTarget(
  editor: Editor,
  shape?: TLShape,
): shape is Trace | TLImageShape | import("../shared/schema").Sheet {
  if (
    !shape ||
    !["sheet", "image", "trace"].includes(shape.type) ||
    editor.getIsReadonly() ||
    editor.isShapeHidden(shape)
  )
    return false;
  let current: TLShape | undefined = shape;
  while (current) {
    if (current.isLocked) return false;
    current = editor.getShape(current.parentId as TLShapeId);
  }
  return true;
}
export function activeTrace(editor: Editor) {
  const id = annotationTargets.get(editor),
    trace = id && editor.getShape(id);
  return trace && trace.type === "trace" && editableTarget(editor, trace)
    ? trace
    : undefined;
}
export function containingTrace(
  editor: Editor,
  shape?: TLShape,
): Trace | undefined {
  while (shape) {
    if (shape.type === "trace") return shape;
    shape = editor.getShape(shape.parentId as TLShapeId);
  }
}
export function putOnTrace(
  editor: Editor,
  source: TLImageShape,
  targetId: TLShapeId,
  region: Rect = fullRegion,
  point?: VecLike,
) {
  const live = editor.getShape<TLImageShape>(source.id),
    target = editor.getShape(targetId);
  if (
    !live ||
    live.type !== "image" ||
    live.props.assetId !== source.props.assetId ||
    JSON.stringify(live.props.crop) !== JSON.stringify(source.props.crop) ||
    live.props.flipX !== source.props.flipX ||
    live.props.flipY !== source.props.flipY ||
    live.props.w !== source.props.w ||
    live.props.h !== source.props.h
  )
    throw new Error("The reference changed. Select it again.");
  const asset = source.props.assetId && editor.getAsset(source.props.assetId);
  if (!asset || asset.type !== "image" || !asset.props.src)
    throw new Error("Wait for the image upload to finish.");
  if (!editableTarget(editor, target) || target.type !== "trace")
    throw new Error("This destination is no longer editable.");
  const crop = traceCrop(
    source.props.crop,
    region,
    source.props.flipX,
    source.props.flipY,
  );
  const rotation = editor.getShapePageTransform(live).rotation();
  const w = source.props.w * region.w,
    h = source.props.h * region.h;
  const id = createShapeId();
  editor.markHistoryStoppingPoint("Put image on tracing paper");
  editor.run(() => {
    const trace = target;
    const center = point
      ? editor.getPointInShapeSpace(trace, point)
      : { x: trace.props.w / 2, y: trace.props.h / 2 };
    const angle = rotation - editor.getShapePageTransform(trace).rotation();
    editor.createShape<TLImageShape>({
      id,
      type: "image",
      parentId: trace.id,
      x: center.x - (w * Math.cos(angle) - h * Math.sin(angle)) / 2,
      y: center.y - (w * Math.sin(angle) + h * Math.cos(angle)) / 2,
      rotation: angle,
      opacity: source.opacity,
      props: { ...source.props, w, h, crop },
      meta: {
        ...(asset.meta?.pdfPages ? { traceSnapshot: true } : {}),
        metersPerUnit: 0,
        calibrationWidth: w,
        calibration: "Not to Scale",
        ...(typeof source.meta.paperRemoval === "number"
          ? { paperRemoval: source.meta.paperRemoval }
          : {}),
      },
    });
    editor.select(id);
  });
  return id;
}
export function duplicateTrace(editor: Editor, trace: Trace) {
  if (!editableTarget(editor, trace)) return;
  annotationTargets.delete(editor);
  editor.setCurrentTool("select");
  editor.markHistoryStoppingPoint("Try another idea");
  editor.run(() => {
    editor.duplicateShapes([trace.id], { x: 0, y: 0 });
    const copy = editor.getOnlySelectedShape();
    if (copy?.type !== "trace" || copy.id === trace.id) return;
    editor.updateShapes([
      { id: trace.id, type: "trace", props: { hidden: true } },
      {
        id: copy.id,
        type: "trace",
        props: { name: `${trace.props.name} — alternative`, hidden: false },
      },
    ]);
  });
}
/** Native tools choose a geometric parent. Explicit annotation context takes precedence. */
export function installTraceAnnotations(editor: Editor) {
  // Same pinned-SDK history boundary as installSourceBehavior.
  const sdk = editor as Editor & { isReplayingHistory(): boolean };
  return editor.sideEffects.registerBeforeCreateHandler(
    "shape",
    (shape, source) => {
      const trace = activeTrace(editor);
      if (
        source !== "user" ||
        sdk.isReplayingHistory() ||
        !trace ||
        !["draw", "geo", "text", "arrow"].includes(shape.type) ||
        !editor.isInAny(
          "draw.drawing",
          "geo.pointing",
          "text.pointing",
          "arrow.pointing",
        )
      )
        return shape;
      const parent = editor.getShape(shape.parentId as TLShapeId);
      const pagePoint = parent
        ? editor.getShapePageTransform(parent).applyToPoint(shape)
        : shape;
      const local = editor.getPointInShapeSpace(trace, pagePoint);
      const parentRotation = parent
        ? editor.getShapePageTransform(parent).rotation()
        : 0;
      return {
        ...shape,
        parentId: trace.id,
        x: local.x,
        y: local.y,
        rotation:
          shape.rotation +
          parentRotation -
          editor.getShapePageTransform(trace).rotation(),
        index: editor.getHighestIndexForParent(trace.id),
      };
    },
  );
}
